import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import { useT } from "../../../../core/i18n";
import Api from "../../../../core/data/redux/utils/apiCall";
import {
  IVF_STATUS_OPTIONS,
  fetchHospitals,
  findAllMedicalAttendance,
  formatSapDate,
  getLoggedInEmpId,
  getStatusBadgeClass,
  getStatusLabel,
  mapApiItemToCard,
  parseLocalCardDate,
  syncMedicalAttendance,
} from "./medicalAttendance.service";

const MedicalAttendanceList: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const routes = all_routes;

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState("");
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const hospitalMapRef = useRef<Record<string, string>>({});

  const loadHospitalMap = useCallback(async (force = false) => {
    if (!force && Object.keys(hospitalMapRef.current).length > 0) return;
    const types = ["62", "63", "66"];
    const map: Record<string, string> = { ...hospitalMapRef.current };
    await Promise.all(
      types.map(async (requestType) => {
        try {
          const options = await fetchHospitals(requestType);
          options.forEach((opt) => {
            if (opt.id) map[opt.id] = opt.label || opt.id;
          });
        } catch {
          /* fall back to raw keys */
        }
      })
    );
    hospitalMapRef.current = map;
  }, []);

  const loadList = useCallback(
    async (overrides?: { status?: string; refreshHospitals?: boolean }) => {
      const statusFilter = overrides?.status ?? status;
      setLoading(true);
      try {
        await loadHospitalMap(!!overrides?.refreshHospitals);
        const empId = getLoggedInEmpId();
        const list = await findAllMedicalAttendance({
          page: 1,
          limit: 100,
          status: statusFilter,
          search: empId,
        });
        const listWithRef = list.filter((item: any) => {
          const ref = item?.ReferenceNumber ?? item?.referenceNumber ?? "";
          return Boolean(String(ref).trim());
        });
        setItems(listWithRef.map((item: any) => mapApiItemToCard(item, hospitalMapRef.current)));
      } catch (err: any) {
        setItems([]);
        toast.error(Api.getErrorMessage(err) || t("failed_to_load_details"));
      } finally {
        setLoading(false);
      }
    },
    [status, t, loadHospitalMap]
  );

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredData = useMemo(() => {
    if (!fromDate && !toDate) return items;
    return items.filter((item) => {
      const itemDate = parseLocalCardDate(String(item.requestCreationDate || ""));
      if (!itemDate) return true;
      if (fromDate) {
        const from = new Date(fromDate);
        from.setHours(0, 0, 0, 0);
        if (itemDate < from) return false;
      }
      if (toDate) {
        const to = new Date(toDate);
        to.setHours(23, 59, 59, 999);
        if (itemDate > to) return false;
      }
      return true;
    });
  }, [items, fromDate, toDate]);

  const handleSync = async () => {
    const empId = getLoggedInEmpId();
    if (!empId) {
      toast.error(t("employee_id_not_found"));
      return;
    }
    setSyncing(true);
    try {
      const res: any = await syncMedicalAttendance(empId);
      toast.success(res?.message || t("synced_successfully") || "Sync successful");
    } catch (err: any) {
      toast.error(Api.getErrorMessage(err) || "Sync failed");
    } finally {
      setSyncing(false);
      await loadList({ refreshHospitals: true });
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">{t("application_prior_approval_ivf_para")}</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.services.list}>Services</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {t("prior_approval_for_ivf_para_6_2_6_3_treatment")}
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleSync}
              disabled={syncing || loading}
            >
              <i className={`ti ti-refresh me-1 ${syncing ? "spin" : ""}`}></i>
              {syncing ? t("syncing") : t("sync")}
            </button>
            <Link to={routes.medicalAttendance.create} className="btn btn-danger">
              <i className="ti ti-plus me-1"></i>
              {t("create_new_request")}
            </Link>
          </div>
        </div>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-3">
                <label className="form-label fw-semibold small">{t("start_date")}</label>
                <input
                  type="date"
                  className="form-control"
                  value={fromDate}
                  max={toDate || new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold small">{t("end_date")}</label>
                <input
                  type="date"
                  className="form-control"
                  value={toDate}
                  min={fromDate || undefined}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (fromDate && value < fromDate) {
                      toast.error("End date cannot be before start date");
                      return;
                    }
                    setToDate(value);
                  }}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold small">{t("filter_by_status")}</label>
                <select
                  className="form-select"
                  value={status}
                  onChange={(e) => {
                    const value = e.target.value;
                    setStatus(value);
                    loadList({ status: value });
                  }}
                >
                  {IVF_STATUS_OPTIONS.map((opt) => {
                    const label = t(opt.labelKey) === opt.labelKey ? opt.label : t(opt.labelKey);
                    return (
                      <option key={opt.key || "all"} value={opt.key}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="col-md-3 d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-danger flex-fill"
                  onClick={() => loadList()}
                  disabled={loading}
                >
                  {t("refresh_list")}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setFromDate("");
                    setToDate("");
                    setStatus("");
                    loadList({ status: "" });
                  }}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-danger" role="status" />
          </div>
        ) : filteredData.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5 text-muted">
              {t("no_records_found")}
            </div>
          </div>
        ) : (
          <div className="row g-3">
            {filteredData.map((item) => {
              const refNo = item.referenceNumber || "-";
              return (
                <div className="col-xl-6" key={item.id}>
                  <div className="card border shadow-sm h-100">
                    <div className="card-header bg-white d-flex justify-content-between align-items-center">
                      <span className="fw-bold">
                        {t("reference_no")}: {refNo}
                      </span>
                      <span className={getStatusBadgeClass(item.requestStatus)}>
                        {getStatusLabel(item.requestStatus, t)}
                      </span>
                    </div>
                    <div className="card-body">
                      <InfoRow label={t("patient_name")} value={item.patientName} />
                      <InfoRow label={t("hospital_name")} value={item.hospitalName} />
                      <InfoRow
                        label={t("request_creation_date")}
                        value={item.requestCreationDate || formatSapDate(item.raw?.created_at)}
                      />
                      <div className="d-flex gap-2 mt-3">
                        <button
                          type="button"
                          className="btn btn-danger flex-fill"
                          onClick={() =>
                            navigate(
                              routes.medicalAttendance.view.replace(
                                ":referenceNumber",
                                encodeURIComponent(refNo)
                              )
                            )
                          }
                        >
                          {t("view_details")}
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger flex-fill"
                          onClick={() =>
                            navigate(
                              routes.medicalAttendance.details.replace(
                                ":referenceNumber",
                                encodeURIComponent(refNo)
                              )
                            )
                          }
                        >
                          {t("display_request")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <style>{`
        .spin { animation: ivf-spin 1s linear infinite; }
        @keyframes ivf-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

const InfoRow: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="d-flex justify-content-between gap-3 mb-2">
    <span className="text-muted fw-semibold">{label}</span>
    <span className="text-dark text-end">{value ?? "-"}</span>
  </div>
);

export default MedicalAttendanceList;
